import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { LoaderCircle, Plus, Save, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { isApiError } from '../../api/client';
import { itemApi, receiptsApi } from '../../api/endpoints';
import { issueMessage } from '../../api/errors';
import {
  activityCodesQuery,
  branchesQuery,
  itemQuery,
  orgCurrenciesQuery,
  paymentMethodsQuery,
  posDevicesQuery,
  queryKeys,
  receiptNextNumberQuery,
  receiptQuery,
  taxesQuery,
} from '../../api/queries';
import type {
  ApiIssue,
  Item,
  OrgActivityCode,
  OrgCurrency,
  OrganizationTax,
  PosDevice,
  ReceiptDetail,
  ReceiptDraftPayload,
  ReceiptLineDraft,
  ReceiptSummary,
} from '../../api/types';
import { useAccess } from '../../app/access';
import { useI18n } from '../../i18n/I18nProvider';
import { clientKey } from '../../lib/hooks';
import { UserText } from '../../ui/Bidi';
import { Button } from '../../ui/Button';
import { AsyncComboBox } from '../../ui/ComboBox';
import { DateField } from '../../ui/DatePicker';
import { DecimalField } from '../../ui/DecimalField';
import { Callout, Skeleton } from '../../ui/Feedback';
import { Money, Page, useDocumentTitle } from '../../ui/Layout';
import { QueryError } from '../../ui/QueryError';
import { SelectField } from '../../ui/Select';
import { TextField } from '../../ui/TextField';
import { useToast } from '../../ui/Toast';
import styles from './Receipt.module.css';
import {
  RECEIPT_TYPES,
  draftFromReceipt,
  isReturnWithReference,
  isReturnWithoutReference,
  issuableTotals,
  receiptBlockers,
  saleDateToIso,
  saleTypeFor,
  type ReceiptBlocker,
} from './receipts';
import { useTaxTypeName } from './ReceiptStatus';
import { RwrFields } from './RwrFields';

/**
 * What a line's product box shows. A catalogue item when the user picks one; when a saved draft is
 * reopened, the line's own saved name (`description`), so the box is never blank (WP-8.9c).
 */
type LineItem = Pick<Item, 'id' | 'nameAr'> &
  Partial<Pick<Item, 'internalCode' | 'unitPrice' | 'currency' | 'defaultTaxIds'>>;

interface LineRow {
  key: string;
  item: LineItem | null;
  line: ReceiptLineDraft;
}

const emptyDraft = (type: string): ReceiptDraftPayload => ({
  receiptType: type,
  currency: 'EGP',
  lines: [],
});

/** Strips empty strings so drafts never carry blank optional fields. */
function cleanDraft(draft: ReceiptDraftPayload): ReceiptDraftPayload {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(draft))
    if (v !== '' && v !== undefined && v !== null) out[k] = v;
  const lines = draft.lines.map((l) => {
    const o: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(l)) if (v !== '' && v !== undefined && v !== null) o[k] = v;
    return o as ReceiptLineDraft;
  });
  return { ...(out as unknown as ReceiptDraftPayload), lines };
}

/** The editor's search: a no-reference return, or a new draft prefilled from an issued receipt. */
export interface ReceiptEditorSearch {
  type?: 'RWR';
  /** Start a corrected receipt from this (invalid or not-accepted) receipt. */
  from?: string;
  /** Start a return with reference of this (valid sale) receipt. */
  returnOf?: string;
}

const UUID = /^[0-9a-f-]{36}$/i;

/** Reads the editor's URL search back, keeping only well-formed values. */
export function parseReceiptEditorSearch(search: Record<string, unknown>): ReceiptEditorSearch {
  const out: ReceiptEditorSearch = {};
  if (search.type === 'RWR') out.type = 'RWR';
  if (typeof search.from === 'string' && UUID.test(search.from)) out.from = search.from;
  if (typeof search.returnOf === 'string' && UUID.test(search.returnOf))
    out.returnOf = search.returnOf;
  return out;
}

/**
 * MF-RCP-003, create and edit an e-receipt draft. Where it is issued from (the POS device, its
 * branch, the activity code), the receipt's own facts (type, date, currency, rate, payment method),
 * the buyer and the lines (item, quantity, price) are entered here. The amounts are the server's:
 * a live summary shows them as the issue will compute them (WP-8.9c). An issued receipt is
 * read-only: the form refuses to open for a non-draft.
 */
export function ReceiptEditorPage({ mode }: { mode: 'new' | 'edit' }) {
  const { t } = useI18n();
  const { orgId = '', receiptId } = useParams({ strict: false }) as {
    orgId?: string;
    receiptId?: string;
  };
  const search = useSearch({ strict: false }) as ReceiptEditorSearch;
  const navigate = useNavigate();
  const access = useAccess(orgId);
  const sourceId = mode === 'new' ? (search.from ?? search.returnOf) : undefined;
  const detail = useQuery({
    ...receiptQuery(orgId, receiptId ?? ''),
    enabled: mode === 'edit' && Boolean(receiptId),
  });
  const source = useQuery({
    ...receiptQuery(orgId, sourceId ?? ''),
    enabled: Boolean(sourceId),
  });
  const orgTaxes = useQuery({ ...taxesQuery(orgId), enabled: Boolean(sourceId) });

  const [draft, setDraft] = useState<ReceiptDraftPayload | null>(() =>
    mode === 'new' && !sourceId ? emptyDraft(search.type === 'RWR' ? 'RWR' : 's') : null,
  );
  const [version, setVersion] = useState(0);
  const [number, setNumber] = useState<string | null>(null);
  const [issues, setIssues] = useState<ApiIssue[]>([]);

  // Edit mode: take the stored draft once; a non-draft redirects to its read-only detail.
  useEffect(() => {
    if (mode !== 'edit' || draft || !detail.data) return;
    const d = detail.data;
    if (d.receipt.status !== 'draft') {
      void navigate({
        to: '/orgs/$orgId/sales/receipts/$receiptId',
        params: { orgId, receiptId: d.receipt.id },
        replace: true,
      });
      return;
    }
    setDraft(d.draft ?? emptyDraft(d.receipt.receiptType));
    setVersion(d.receipt.version);
    setNumber(d.receipt.receiptNumber);
  }, [mode, draft, detail.data, navigate, orgId]);

  // A prefilled new draft (a corrected receipt, or a return of a sale), taken once.
  useEffect(() => {
    if (!sourceId || draft || !source.data || orgTaxes.isPending) return;
    setDraft(
      draftFromReceipt(source.data, search.returnOf ? 'return' : 'correction', orgTaxes.data),
    );
  }, [sourceId, draft, source.data, search.returnOf, orgTaxes.isPending, orgTaxes.data]);

  useDocumentTitle(
    number
      ? t.receipts.editor.editTitle(number)
      : draft && isReturnWithoutReference(draft.receiptType)
        ? t.receipts.editor.returnTitle
        : t.receipts.editor.newTitle,
  );

  // A user who may not write receipts sees the refusal, not a fillable form (RB3R8-13).
  if (access.isLoaded && !access.can('receipt.create'))
    return (
      <Page>
        <Callout tone="info" role="note">
          <span data-testid="editor-readonly">{t.receipts.editor.noPermission}</span>
        </Callout>
        <p className={styles.backLine}>
          <Link to="/orgs/$orgId/sales/receipts" params={{ orgId }}>
            {t.receipts.editor.backToList}
          </Link>
        </p>
      </Page>
    );
  if (mode === 'edit' && detail.isError)
    return (
      <Page>
        <QueryError error={detail.error} onRetry={() => void detail.refetch()} />
      </Page>
    );
  if (sourceId && source.isError)
    return (
      <Page>
        <QueryError error={source.error} onRetry={() => void source.refetch()} />
      </Page>
    );
  if (!draft)
    return (
      <Page>
        <Skeleton height="12rem" />
      </Page>
    );

  return (
    <Editor
      orgId={orgId}
      receiptId={receiptId ?? null}
      draft={draft}
      setDraft={setDraft}
      version={version}
      setVersion={setVersion}
      number={number}
      setNumber={setNumber}
      issues={issues}
      setIssues={setIssues}
      knownDevice={detail.data?.device ?? source.data?.device ?? null}
      source={
        source.data ? { detail: source.data, as: search.returnOf ? 'return' : 'correction' } : null
      }
    />
  );
}

function Editor({
  orgId,
  receiptId,
  draft,
  setDraft,
  version,
  setVersion,
  number,
  setNumber,
  issues,
  setIssues,
  knownDevice,
  source,
}: {
  orgId: string;
  receiptId: string | null;
  draft: ReceiptDraftPayload;
  setDraft: (next: ReceiptDraftPayload) => void;
  version: number;
  setVersion: (n: number) => void;
  number: string | null;
  setNumber: (n: string | null) => void;
  issues: ApiIssue[];
  setIssues: (issues: ApiIssue[]) => void;
  /** The draft's device as the read model names it (it may no longer be in service). */
  knownDevice: ReceiptDetail['device'];
  /** The receipt a prefilled draft comes from. */
  source: { detail: ReceiptDetail; as: 'correction' | 'return' } | null;
}) {
  const { t, pick } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const activities = useQuery(activityCodesQuery(orgId));
  const branches = useQuery(branchesQuery(orgId));
  const currencies = useQuery(orgCurrenciesQuery(orgId));
  const devices = useQuery(posDevicesQuery(orgId));
  const paymentMethods = useQuery(paymentMethodsQuery);
  const orgTaxes = useQuery(taxesQuery(orgId));
  const access = useAccess(orgId);

  const [lines, setLines] = useState<LineRow[]>(() =>
    draft.lines.map((line) => ({
      key: clientKey('line'),
      // A saved line shows its saved name (WP-8.9c, RB3R8-03): never a blank product box.
      item: line.itemId && line.description ? { id: line.itemId, nameAr: line.description } : null,
      line,
    })),
  );

  // A reopened draft can carry a line by its item id with no saved name — an imported or API-made
  // draft (the editor's own picks always save the name). Resolve the catalogue item so the product
  // box is never blank on load (U-2), the display the correction/return prefill already seeds. Only
  // lines still without a shown item are looked up.
  const unresolvedItemIds = useMemo(
    () => [...new Set(lines.flatMap((r) => (!r.item && r.line.itemId ? [r.line.itemId] : [])))],
    [lines],
  );
  const resolvedItems = useQueries({
    queries: unresolvedItemIds.map((id) => itemQuery(orgId, id)),
  });
  const resolvedNames = useMemo(() => {
    const names = new Map<string, string>();
    unresolvedItemIds.forEach((id, i) => {
      const data = resolvedItems[i]?.data;
      if (data) names.set(id, data.nameAr);
    });
    return names;
  }, [resolvedItems, unresolvedItemIds]);
  useEffect(() => {
    if (resolvedNames.size === 0) return;
    setLines((rows) => {
      let changed = false;
      const next = rows.map((row) => {
        const id = row.line.itemId;
        if (row.item || !id) return row;
        const name = resolvedNames.get(id);
        if (name === undefined) return row;
        changed = true;
        return { ...row, item: { id, nameAr: name } };
      });
      // A fresh array every render; the no-op guard (same ref back) stops a re-render loop.
      return changed ? next : rows;
    });
  }, [resolvedNames]);

  const setField = <K extends keyof ReceiptDraftPayload>(key: K, value: ReceiptDraftPayload[K]) =>
    setDraft({ ...draft, [key]: value } as ReceiptDraftPayload);
  const setBuyer = (patch: Partial<NonNullable<ReceiptDraftPayload['buyer']>>) =>
    setDraft({ ...draft, buyer: { type: 'P', ...draft.buyer, ...patch } });

  /** The device decides the branch: one device belongs to one branch, so they never disagree. */
  const chooseDevice = (device: PosDevice | undefined) =>
    setDraft({ ...draft, posDeviceId: device?.id, branchId: device?.branchId });

  // Defaults on a new receipt (2.01): the primary activity and the base currency. The device is
  // always the user's explicit choice: it decides the branch and the number series.
  useEffect(() => {
    if (receiptId) return;
    let next = draft;
    const primary = activities.data?.find((a) => a.isPrimary)?.code;
    if (!next.activityCode && primary) next = { ...next, activityCode: primary };
    const base = currencies.data?.find((c) => c.isPrimary)?.code ?? currencies.data?.[0]?.code;
    if (!next.currency && base) next = { ...next, currency: base };
    if (next !== draft) setDraft(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiptId, activities.data, currencies.data]);

  const nextNumber = useQuery({
    ...receiptNextNumberQuery(orgId, draft.posDeviceId ?? ''),
    enabled: !receiptId && Boolean(draft.posDeviceId),
  });

  const blockers = useMemo(() => receiptBlockers(draft), [draft]);
  // Each server issue is also shown on its own control (RB3R8-12), keyed by the draft's path.
  const fieldErrors = useMemo(() => {
    const byPath: Record<string, string> = {};
    for (const issue of issues) {
      const path = issue.path.replace(/^draft\./, '');
      byPath[path] ??= issueMessage(t, issue);
    }
    return byPath;
  }, [issues, t]);
  const errorOf = (...paths: string[]) =>
    paths.map((path) => fieldErrors[path]).find(Boolean) ?? undefined;
  const returnWithReference = isReturnWithReference(draft.receiptType);
  const withoutReference = isReturnWithoutReference(draft.receiptType);

  const lineDraft: ReceiptDraftPayload = { ...draft, lines: lines.map((row) => row.line) };

  // The live summary (RB3R8-02): the server's own calculation of this draft, debounced.
  const body = useMemo(() => cleanDraft(lineDraft), [JSON.stringify(lineDraft)]); // eslint-disable-line react-hooks/exhaustive-deps
  const [debounced, setDebounced] = useState(body);
  useEffect(() => {
    const h = setTimeout(() => setDebounced(body), 350);
    return () => clearTimeout(h);
  }, [body]);
  const preview = useQuery({
    queryKey: [...queryKeys.org(orgId), 'receipts', 'preview', JSON.stringify(debounced)],
    queryFn: ({ signal }) => receiptsApi.preview(orgId, debounced, signal),
    enabled: debounced.lines.length > 0,
    placeholderData: keepPreviousData,
  });

  const save = useMutation({
    mutationFn: () =>
      receiptId
        ? receiptsApi.update(orgId, receiptId, version, cleanDraft(lineDraft))
        : receiptsApi.create(orgId, cleanDraft(lineDraft)),
    onSuccess: async (res) => {
      setIssues([]);
      setVersion(res.version);
      setNumber(res.receiptNumber);
      await queryClient.invalidateQueries({ queryKey: [...queryKeys.org(orgId), 'receipts'] });
      toast.success(t.receipts.editor.saved);
      void navigate({
        to: '/orgs/$orgId/sales/receipts/$receiptId',
        params: { orgId, receiptId: res.id },
      });
    },
    onError: (error) => {
      if (isApiError(error) && error.code === 'validation_error' && error.issues.length) {
        setIssues(error.issues);
        return;
      }
      toast.error(error);
    },
  });

  const addLine = () =>
    setLines((rows) => [...rows, { key: clientKey('line'), item: null, line: { quantity: '1' } }]);
  const removeLine = (key: string) => setLines((rows) => rows.filter((r) => r.key !== key));
  const patchLine = (key: string, patch: Partial<ReceiptLineDraft>) =>
    setLines((rows) =>
      rows.map((r) =>
        r.key === key
          ? {
              ...r,
              line: Object.fromEntries(
                Object.entries({ ...r.line, ...patch }).filter(
                  ([, v]) => v !== '' && v !== undefined,
                ),
              ) as ReceiptLineDraft,
            }
          : r,
      ),
    );
  /** A fixed fee's amount for one line (only fixed subtypes have one; a rate tax never does). */
  const setFixedAmount = (key: string, taxId: string, amount: string) =>
    setLines((rows) =>
      rows.map((r) => {
        if (r.key !== key) return r;
        const amounts = Object.fromEntries(
          Object.entries({ ...r.line.fixedTaxAmounts, [taxId]: amount }).filter(([, v]) => v),
        );
        const { fixedTaxAmounts: _old, ...rest } = r.line;
        return {
          ...r,
          line: Object.keys(amounts).length ? { ...rest, fixedTaxAmounts: amounts } : rest,
        };
      }),
    );
  // The preview's own findings on a field (a fee amount still missing), keyed like the draft.
  const previewIssueOf = (path: string) => {
    const issue = preview.data?.issues.find((i) => i.path === path);
    return issue ? issueMessage(t, issue) : undefined;
  };

  const pickItem = (key: string, item: LineItem) => {
    // A reopened line's own saved name is offered back as a choice; picking it changes nothing.
    if (item.currency === undefined) return;
    setLines((rows) =>
      rows.map((r) => {
        if (r.key !== key) return r;
        const unitPrice = item.currency === draft.currency ? item.unitPrice : undefined;
        // A new item brings its own taxes; a fee amount typed for the previous item does not carry.
        const { fixedTaxAmounts: _previous, ...rest } = r.line;
        return {
          ...r,
          item,
          line: {
            ...rest,
            itemId: item.id,
            description: item.nameAr,
            quantity: r.line.quantity ?? '1',
            ...(unitPrice ? { unitPrice } : {}),
            taxIds: item.defaultTaxIds ?? [],
          },
        };
      }),
    );
  };

  const currency = draft.currency;
  const label = t.receipts.types as Record<string, string | undefined>;
  const deviceList = devices.data ?? [];
  const deviceOptions = [
    ...deviceList,
    ...(knownDevice && !deviceList.some((d) => d.id === knownDevice.id)
      ? [{ ...knownDevice, branchName: '' }]
      : []),
  ];
  const branch = branches.data?.find((b) => b.id === draft.branchId);
  const noDevice = devices.isSuccess && deviceList.length === 0 && !draft.posDeviceId;
  const sourceNumber = source?.detail.receipt.receiptNumber ?? '';

  return (
    <Page>
      <section className={styles.hero}>
        <div className={styles.heroRow}>
          <div className={styles.heroText}>
            <span className={styles.heroEyebrow}>{t.receipts.title}</span>
            <h1 className={styles.heroTitle} data-testid="editor-title">
              {number
                ? t.receipts.editor.editTitle(number)
                : withoutReference
                  ? t.receipts.editor.returnTitle
                  : t.receipts.editor.newTitle}
            </h1>
            {/* The number is in the title once saved (U-11); before, where it will come from. */}
            {!number ? (
              <p className={styles.heroSub} data-testid="editor-number">
                {t.receipts.editor.numberProvisional}
                {nextNumber.data?.number
                  ? ` · ${t.receipts.editor.numberNext(nextNumber.data.number)}`
                  : ''}
              </p>
            ) : null}
          </div>
        </div>
      </section>

      {source ? (
        <Callout tone="info" role="note">
          <span data-testid="editor-source" data-as={source.as}>
            {source.as === 'return'
              ? t.receipts.editor.returnOf(sourceNumber)
              : t.receipts.editor.correctionOf(sourceNumber)}
          </span>
        </Callout>
      ) : null}

      {blockers.length ? (
        <Callout tone="warning" role="note" className={styles.blockers}>
          <ul className={styles.blockerList} data-testid="editor-blockers">
            {blockers.map((b) => (
              <li key={b} data-blocker={b}>
                {b === 'pos' && noDevice ? t.receipts.editor.missingPosNone : blockerMessage(t, b)}
              </li>
            ))}
          </ul>
        </Callout>
      ) : null}

      {issues.length ? (
        <Callout tone="danger" role="alert" title={t.errors.summaryTitle(issues.length)}>
          <ul className={styles.blockerList} data-testid="editor-issues">
            {issues.map((issue, i) => (
              <li key={`${issue.path}-${i}`} data-path={issue.path}>
                <span className={styles.issueField}>{issueField(t, issue.path)}:</span>{' '}
                {issueMessage(t, issue)}
              </li>
            ))}
          </ul>
        </Callout>
      ) : null}

      {/* Where the receipt is issued from, in dependency order: the device decides its branch (shown,
          never chosen), then the activity (WP-8.9 E-1, WP-8.9c E-5). No group is "settings". */}
      <section className={styles.card} data-testid="editor-issued-from">
        <h2 className={styles.cardTitle}>{t.receipts.editor.issuedFromTitle}</h2>
        {noDevice ? (
          // Whoever can set the organization up is told a device must be registered; everyone else
          // is asked to have an administrator do it, never pointed at a page they cannot use or that
          // is not built yet (RB3R8-30). Neither message is a link.
          <p className={styles.noDevice} data-testid="editor-no-device">
            {access.can('settings.manage')
              ? t.receipts.editor.noDevice
              : t.receipts.editor.noDeviceStaff}
          </p>
        ) : null}
        <div className={styles.formGrid}>
          <SelectField
            label={t.receipts.editor.pos}
            fieldName="posDeviceId"
            isRequired
            isDisabled={Boolean(receiptId) || noDevice}
            description={t.receipts.editor.posHint}
            placeholder={t.receipts.editor.choose}
            options={deviceOptions.map((d) => ({
              id: d.id,
              label: d.name,
              description: d.branchName ? `${d.serial} · ${d.branchName}` : d.serial,
            }))}
            value={draft.posDeviceId ?? null}
            errorMessage={errorOf('posDeviceId')}
            onChange={(value) => chooseDevice(deviceList.find((d) => d.id === value))}
          />
          <div className={styles.readField} data-field="branchId" data-testid="editor-branch">
            <span className={styles.readLabel}>{t.receipts.editor.branch}</span>
            <span className={styles.readValue}>
              {branch ? (
                <>
                  <UserText>{branch.name}</UserText>
                  <span className={styles.readMeta}>
                    {t.receipts.editor.branchCode(String(branch.eta_branch_id))}
                  </span>
                </>
              ) : (
                <span className={styles.subtle}>{t.receipts.editor.branchNone}</span>
              )}
            </span>
            <span className={styles.readHint}>{t.receipts.editor.branchFromDevice}</span>
            {errorOf('branchId') ? (
              <span className={styles.readError} role="alert" data-testid="editor-branch-error">
                {errorOf('branchId')}
              </span>
            ) : null}
          </div>
          <SelectField
            label={t.receipts.editor.activityCode}
            fieldName="activityCode"
            isRequired
            placeholder={t.receipts.editor.choose}
            options={(activities.data ?? []).map((a: OrgActivityCode) => ({
              id: a.code,
              label: `${a.code} — ${pick(a.nameAr, a.nameEn)}`,
            }))}
            value={draft.activityCode ?? null}
            errorMessage={errorOf('activityCode')}
            onChange={(value) => setField('activityCode', value ?? undefined)}
          />
        </div>
      </section>

      <section className={styles.card} data-testid="editor-receipt">
        <h2 className={styles.cardTitle}>{t.receipts.editor.receiptTitle}</h2>
        <div className={styles.formGrid}>
          {receiptId ? (
            // A saved receipt keeps its type: shown as a value, not as a control (RB3R8-14).
            <div className={styles.readField} data-field="receiptType">
              <span className={styles.readLabel}>{t.receipts.editor.type}</span>
              <span className={styles.readValue}>
                {label[draft.receiptType] ?? draft.receiptType}
              </span>
              <span className={styles.readHint}>{t.receipts.editor.typeLocked}</span>
            </div>
          ) : (
            <SelectField
              label={t.receipts.editor.type}
              fieldName="receiptType"
              isRequired
              placeholder={t.receipts.editor.choose}
              options={RECEIPT_TYPES.map((type) => ({
                id: type.code,
                label: label[type.code] ?? type.code,
              }))}
              value={draft.receiptType}
              errorMessage={errorOf('receiptType')}
              onChange={(value) => value && setField('receiptType', value)}
            />
          )}
          <DateField
            label={t.receipts.editor.issueDate}
            fieldName="issueDate"
            description={t.receipts.editor.issueDateHint}
            value={draft.issueDate ?? null}
            errorMessage={errorOf('issueDate')}
            onChange={(issueDate) => setField('issueDate', issueDate ?? undefined)}
          />
          <SelectField
            label={t.receipts.editor.currency}
            fieldName="currency"
            isRequired
            placeholder={t.receipts.editor.choose}
            options={(currencies.data ?? []).map((c: OrgCurrency) => ({
              id: c.code,
              label: `${c.code} — ${pick(c.nameAr, c.nameEn)}`,
            }))}
            value={draft.currency}
            errorMessage={errorOf('currency')}
            onChange={(value) => value && setField('currency', value)}
          />
          {currency !== 'EGP' ? (
            <DecimalField
              label={t.receipts.editor.exchangeRate}
              fieldName="exchangeRate"
              isRequired
              description={t.receipts.editor.rateHint(currency)}
              value={draft.exchangeRate ?? ''}
              errorMessage={errorOf('exchangeRate')}
              onChange={(exchangeRate) => setField('exchangeRate', exchangeRate || undefined)}
            />
          ) : null}
          <SelectField
            label={t.receipts.editor.paymentMethod}
            fieldName="paymentMethod"
            description={t.receipts.editor.paymentMethodHint}
            placeholder={t.receipts.editor.choose}
            options={(paymentMethods.data ?? []).map((m) => ({
              id: m.code,
              label: t.receipts.paymentMethods[m.code] ?? m.nameEn,
              description: m.code,
            }))}
            value={draft.paymentMethod ?? null}
            errorMessage={errorOf('paymentMethod')}
            onChange={(value) => setField('paymentMethod', value ?? undefined)}
          />
        </div>
      </section>

      {withoutReference ? (
        <RwrFields
          reason={draft.documentUseReason ?? null}
          saleDate={draft.salesIssuedDateTime ? draft.salesIssuedDateTime.slice(0, 10) : null}
          onReason={(documentUseReason) =>
            setField('documentUseReason', documentUseReason ?? undefined)
          }
          onSaleDate={(date) =>
            setField('salesIssuedDateTime', date ? saleDateToIso(date) : undefined)
          }
          reasonError={errorOf('documentUseReason')}
          saleDateError={errorOf('salesIssuedDateTime')}
        />
      ) : null}

      {returnWithReference ? (
        <section className={styles.card} data-testid="editor-reference">
          <h2 className={styles.cardTitle}>{t.receipts.editor.returnTitleField}</h2>
          <div className={styles.formGrid}>
            <ReferencePicker
              orgId={orgId}
              returnType={draft.receiptType}
              value={draft.referenceUUID ?? null}
              errorMessage={errorOf('referenceUUID')}
              onChange={(uuid) => setField('referenceUUID', uuid ?? undefined)}
            />
          </div>
        </section>
      ) : null}

      <section className={styles.card}>
        <h2 className={styles.cardTitle}>{t.receipts.editor.buyerTitle}</h2>
        <div className={styles.formGrid}>
          <SelectField
            label={t.receipts.editor.buyerType}
            fieldName="buyerType"
            options={[
              { id: 'B', label: t.receipts.editor.buyerTypes.B },
              { id: 'P', label: t.receipts.editor.buyerTypes.P },
              { id: 'F', label: t.receipts.editor.buyerTypes.F },
            ]}
            value={draft.buyer?.type ?? 'P'}
            errorMessage={errorOf('buyer.type', 'buyer')}
            onChange={(value) => value && setBuyer({ type: value as 'B' | 'P' | 'F' })}
          />
          <TextField
            label={t.receipts.editor.buyerId}
            fieldName="buyerId"
            dir="ltr"
            value={draft.buyer?.id ?? ''}
            errorMessage={errorOf('buyer.id')}
            onChange={(id) => setBuyer({ id: id || undefined })}
          />
          <TextField
            label={t.receipts.editor.buyerName}
            fieldName="buyerName"
            value={draft.buyer?.name ?? ''}
            errorMessage={errorOf('buyer.name')}
            onChange={(name) => setBuyer({ name: name || undefined })}
          />
          <TextField
            label={t.receipts.editor.buyerMobile}
            fieldName="buyerMobile"
            dir="ltr"
            value={draft.buyer?.mobile ?? ''}
            errorMessage={errorOf('buyer.mobile')}
            onChange={(mobile) => setBuyer({ mobile: mobile || undefined })}
          />
        </div>
      </section>

      <section className={styles.card}>
        <h2 className={styles.cardTitle}>{t.receipts.editor.linesTitle}</h2>
        {lines.length ? (
          // On phones each line stacks: the item on its own row, then quantity, price and remove,
          // each with its visible name (the header row is hidden there).
          <table className={`${styles.lineTable} ${styles.editLines}`} data-testid="editor-lines">
            <caption className="visually-hidden">{t.receipts.editor.linesTitle}</caption>
            <thead>
              <tr>
                <th>#</th>
                <th>{t.receipts.editor.item}</th>
                <th className={styles.end}>{t.receipts.editor.quantity}</th>
                <th className={styles.end}>{t.receipts.editor.unitPrice(currency)}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {lines.map((row, index) => (
                <tr key={row.key} data-testid={`line-${index + 1}`}>
                  <td>{index + 1}</td>
                  <td>
                    <AsyncComboBox<LineItem>
                      aria-label={t.receipts.editor.item}
                      label=""
                      fieldName={`lines.${index}.item`}
                      value={row.line.itemId ?? null}
                      selectedItem={row.item}
                      errorMessage={errorOf(
                        `lines.${index}.itemId`,
                        `lines.${index}.description`,
                        `lines.${index}.taxIds`,
                        `lines.${index}`,
                      )}
                      onChange={(item) => item && pickItem(row.key, item)}
                      queryFor={(q) => ({
                        queryKey: [...queryKeys.org(orgId), 'items', 'receipt-line', q],
                        queryFn: async ({ signal }) =>
                          (await itemApi.list(orgId, { q, limit: 20 }, signal)).items,
                      })}
                      getKey={(item) => item.id}
                      getLabel={(item) => item.nameAr}
                      renderItem={(item) => (
                        <span>
                          <span className={styles.optionTitle}>{item.nameAr}</span>
                          {item.internalCode ? (
                            <span className={styles.optionMeta}>
                              {item.internalCode} · {item.unitPrice} {item.currency}
                            </span>
                          ) : null}
                        </span>
                      )}
                      placeholder={t.receipts.editor.choose}
                      isClearable
                    />
                    <LineTaxes
                      index={index}
                      taxIds={row.line.taxIds ?? row.item?.defaultTaxIds ?? []}
                      taxes={orgTaxes.data ?? []}
                      amounts={row.line.fixedTaxAmounts ?? {}}
                      currency={currency}
                      errorOf={(path) => errorOf(path) ?? previewIssueOf(path)}
                      onAmount={(taxId, amount) => setFixedAmount(row.key, taxId, amount)}
                    />
                  </td>
                  <td className={styles.end}>
                    <span className={styles.lineLabel} aria-hidden="true">
                      {t.receipts.editor.quantity}
                    </span>
                    <DecimalField
                      label=""
                      aria-label={t.receipts.editor.quantity}
                      fieldName={`lines.${index}.quantity`}
                      value={row.line.quantity ?? ''}
                      errorMessage={errorOf(`lines.${index}.quantity`)}
                      onChange={(quantity) => patchLine(row.key, { quantity })}
                    />
                  </td>
                  <td className={styles.end}>
                    <span className={styles.lineLabel} aria-hidden="true">
                      {t.receipts.editor.unitPrice(currency)}
                    </span>
                    <DecimalField
                      label=""
                      aria-label={t.receipts.editor.unitPrice(currency)}
                      fieldName={`lines.${index}.unitPrice`}
                      value={row.line.unitPrice ?? ''}
                      errorMessage={errorOf(`lines.${index}.unitPrice`)}
                      onChange={(unitPrice) => patchLine(row.key, { unitPrice })}
                    />
                  </td>
                  <td className={styles.end}>
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<Trash2 size={15} aria-hidden="true" />}
                      aria-label={t.receipts.editor.removeLine}
                      testId={`remove-line-${index + 1}`}
                      onPress={() => removeLine(row.key)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className={styles.subtle} data-testid="editor-no-lines">
            {t.receipts.editor.noLines}
          </p>
        )}
        <div className={styles.lineActions}>
          <Button
            variant="secondary"
            size="sm"
            icon={<Plus size={15} aria-hidden="true" />}
            testId="add-line"
            onPress={addLine}
          >
            {t.receipts.editor.addLine}
          </Button>
        </div>
      </section>

      <AmountsSummary
        preview={debounced.lines.length > 0 ? preview.data : undefined}
        isFetching={preview.isFetching}
        hasLines={lines.length > 0}
        isError={preview.isError}
      />

      <div className={styles.editorActions}>
        <Button
          variant="ghost"
          onPress={() => void navigate({ to: '/orgs/$orgId/sales/receipts', params: { orgId } })}
        >
          {t.common.cancel}
        </Button>
        <Button
          variant="primary"
          icon={save.isPending ? <LoaderCircle size={16} /> : <Save size={16} aria-hidden="true" />}
          isPending={save.isPending}
          isDisabled={blockers.length > 0}
          testId="save-draft"
          onPress={() => save.mutate()}
        >
          {save.isPending ? t.receipts.editor.saving : t.receipts.editor.saveDraft}
        </Button>
      </div>
    </Page>
  );
}

/**
 * The amounts of the draft as the issue will compute them (RB3R8-02): the server's own resolution
 * and engine (`POST …/receipts/preview`), in the receipt's currency. Never a browser formula.
 */
function AmountsSummary({
  preview,
  isFetching,
  hasLines,
  isError,
}: {
  preview: Awaited<ReturnType<typeof receiptsApi.preview>> | undefined;
  isFetching: boolean;
  hasLines: boolean;
  isError: boolean;
}) {
  const { t } = useI18n();
  const taxName = useTaxTypeName();
  const totals = issuableTotals(preview);
  return (
    <section className={styles.card} data-testid="editor-summary" aria-live="polite">
      <h2 className={styles.cardTitle}>
        {t.receipts.editor.summaryTitle}
        {isFetching ? (
          <span className={styles.subtle}> · {t.receipts.editor.summaryUpdating}</span>
        ) : null}
      </h2>
      {!hasLines ? (
        <p className={styles.subtle}>{t.receipts.editor.summaryEmpty}</p>
      ) : totals && preview ? (
        <dl className={styles.kv}>
          <dt>{t.receipts.detail.totalSales}</dt>
          <dd>
            <Money value={totals.totalSales} currency={preview.currency} />
          </dd>
          <dt>{t.receipts.detail.netAmount}</dt>
          <dd>
            <Money value={totals.netAmount} currency={preview.currency} />
          </dd>
          {preview.taxTotals.map((tax) => (
            <TaxRow
              key={tax.taxType}
              name={taxName(tax.taxType)}
              amount={tax.amount}
              currency={preview.currency}
            />
          ))}
          <dt className={styles.totalTerm}>{t.receipts.detail.totalAmount}</dt>
          <dd>
            <Money
              value={totals.totalAmount}
              currency={preview.currency}
              testId="editor-summary-total"
            />
          </dd>
        </dl>
      ) : (
        <p className={styles.subtle}>
          {isError ? t.receipts.confirm.noAmounts : t.receipts.editor.summaryIncomplete}
        </p>
      )}
      <p className={styles.totalsNote}>{t.receipts.editor.summaryNote}</p>
    </section>
  );
}

function TaxRow({ name, amount, currency }: { name: string; amount: string; currency: string }) {
  return (
    <>
      <dt>{name}</dt>
      <dd>
        <Money value={amount} currency={currency} />
      </dd>
    </>
  );
}

/**
 * A return's sale receipt, picked from this organization's valid sale receipts of the matching type
 * (RB3R8-07): searched by number or buyer, or by date when the text is a date. The API field stays
 * the sale's uuid.
 */
/**
 * The taxes a line carries (its item's defaults, or the saved line's), as the issue will apply them
 * (DEC-111): a rate subtype shows its rate and takes no amount; a fixed subtype (a stamp or fee
 * "(amount)", T3 …) takes the line's amount and shows no rate. The amount is required for the issue
 * and never negative; the server's engine decides the rest.
 */
function LineTaxes({
  index,
  taxIds,
  taxes,
  amounts,
  currency,
  errorOf,
  onAmount,
}: {
  index: number;
  taxIds: string[];
  taxes: OrganizationTax[];
  amounts: Record<string, string>;
  currency: string;
  errorOf: (path: string) => string | undefined;
  onAmount: (taxId: string, amount: string) => void;
}) {
  const { t, pick, formatNumber } = useI18n();
  if (!taxIds.length) return null;
  return (
    <div className={styles.lineTaxes} data-testid={`line-${index + 1}-taxes`}>
      <span className={styles.lineTaxesLabel}>{t.receipts.editor.lineTaxes}</span>
      <ul className={styles.lineTaxList}>
        {taxIds.map((id) => {
          const tax = taxes.find((x) => x.id === id);
          if (!tax) return null;
          const name = pick(tax.nameAr, tax.nameEn);
          const code = `${tax.taxType} · ${tax.taxSubtype}`;
          if (tax.kind === 'fixed') {
            const path = `lines.${index}.fixedTaxAmounts.${id}`;
            return (
              <li key={id} data-kind="fixed" data-tax={tax.taxType}>
                <DecimalField
                  label={t.receipts.editor.feeAmount(name, currency)}
                  fieldName={path}
                  isRequired
                  description={t.receipts.editor.feeAmountHint(code)}
                  value={amounts[id] ?? ''}
                  errorMessage={errorOf(path)}
                  onChange={(amount) => onAmount(id, amount)}
                />
              </li>
            );
          }
          return (
            <li key={id} data-kind="rate" data-tax={tax.taxType} className={styles.lineTaxRate}>
              <UserText>{name}</UserText>{' '}
              <span className={styles.num}>
                {t.receipts.editor.taxRate(formatNumber(tax.rate ?? '0'))}
              </span>{' '}
              <span className={styles.readMeta}>{code}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ReferencePicker({
  orgId,
  returnType,
  value,
  onChange,
  errorMessage,
}: {
  orgId: string;
  returnType: string;
  value: string | null;
  onChange: (uuid: string | null) => void;
  errorMessage?: string | undefined;
}) {
  const { t, formatDate } = useI18n();
  const saleType = saleTypeFor(returnType);
  // The chosen sale, read back by its uuid (a reopened draft, or a return started from the sale).
  const chosen = useQuery({
    queryKey: [...queryKeys.org(orgId), 'receipts', 'by-uuid', value],
    queryFn: async ({ signal }) =>
      (await receiptsApi.list(orgId, { uuid: value ?? '', limit: 1 }, signal)).items[0] ?? null,
    enabled: Boolean(value),
  });
  // The input holds the number only: a left-to-right token joined to a date and an Arabic name in
  // one plain-text value is reordered by the bidi algorithm. The chosen sale's date and buyer are
  // said beneath the field instead.
  const picked = chosen.data ?? null;
  return (
    <AsyncComboBox<ReceiptSummary>
      label={t.receipts.editor.referenceUuid}
      fieldName="referenceUUID"
      isRequired
      errorMessage={errorMessage}
      description={
        picked ? (
          <span data-testid="editor-reference-chosen">
            {t.receipts.editor.referenceChosen}{' '}
            <bdi>{picked.date ? formatDate(picked.date) : '—'}</bdi>
            {picked.buyerName ? (
              <>
                {' · '}
                <UserText>{picked.buyerName}</UserText>
              </>
            ) : null}
          </span>
        ) : (
          t.receipts.editor.referenceUuidHint
        )
      }
      placeholder={t.receipts.editor.choose}
      value={value}
      selectedItem={chosen.data ?? null}
      onChange={(r) => onChange(r?.uuid ?? null)}
      queryFor={(q) => ({
        queryKey: [...queryKeys.org(orgId), 'receipts', 'reference', saleType, q],
        queryFn: async ({ signal }) => {
          const date = /^\d{4}-\d{2}-\d{2}$/.test(q) ? q : null;
          return (
            await receiptsApi.list(
              orgId,
              {
                status: 'issued',
                etaStatus: 'valid',
                ...(saleType ? { receiptType: saleType } : {}),
                ...(date ? { from: date, to: date } : q ? { q } : {}),
                sort: 'date',
                dir: 'desc',
                limit: 20,
              },
              signal,
            )
          ).items.filter((r) => r.uuid);
        },
      })}
      getKey={(r) => r.uuid ?? r.id}
      getLabel={(r) => r.receiptNumber ?? '—'}
      renderItem={(r) => (
        <span>
          <span className={styles.optionTitle}>{r.receiptNumber}</span>
          <span className={styles.optionMeta}>
            {[r.date ? formatDate(r.date) : null, r.buyerName].filter(Boolean).join(' · ')}
          </span>
        </span>
      )}
      isClearable
    />
  );
}

function blockerMessage(t: ReturnType<typeof useI18n>['t'], blocker: ReceiptBlocker): string {
  switch (blocker) {
    case 'type':
      return t.receipts.editor.missingType;
    case 'pos':
      return t.receipts.editor.missingPos;
    case 'rwrReason':
      return t.receipts.editor.rwrMissingReason;
    case 'rwrSaleDate':
      return t.receipts.editor.rwrMissingDate;
    case 'reference':
      return t.receipts.editor.referenceUuidRequired;
  }
}

/** The field a server issue is about, in words (RB3R8-12): never the raw key alone. */
function issueField(t: ReturnType<typeof useI18n>['t'], rawPath: string): string {
  const e = t.receipts.editor;
  // Input checks report the request's own path (`draft.buyer.id`); the service, the draft's.
  const path = rawPath.replace(/^draft\./, '');
  const fields: Record<string, string> = {
    posDeviceId: e.pos,
    branchId: e.branch,
    activityCode: e.activityCode,
    receiptType: e.type,
    issueDate: e.issueDate,
    currency: e.currency,
    exchangeRate: e.exchangeRate,
    paymentMethod: e.paymentMethod,
    referenceUUID: e.referenceUuid,
    documentUseReason: e.documentUseReason,
    salesIssuedDateTime: e.salesIssuedDateTime,
    'buyer.type': e.buyerType,
    'buyer.id': e.buyerId,
    'buyer.name': e.buyerName,
    'buyer.mobile': e.buyerMobile,
    lines: e.linesTitle,
  };
  if (fields[path]) return fields[path];
  const line = /^lines\.(\d+)\.(\w+)/.exec(path);
  if (line) {
    const name =
      line[2] === 'quantity'
        ? e.quantity
        : line[2] === 'unitPrice'
          ? e
              .unitPrice('')
              .replace(/\s*\(\)\s*$/, '')
              .trim()
          : e.item;
    return e.linePath(String(Number(line[1]) + 1), name);
  }
  return rawPath;
}
